import { useEffect, useState, useRef } from "react";
import { useUser } from "../context/UserContext";
import { supabase } from "../lib/supabase";
import NewMessageModal from "./NewMessageModal";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faEllipsisH,
    faThumbtack,
    faTrash,
    faCircle,
} from "@fortawesome/free-solid-svg-icons";
import { faCircle as faCircleRegular } from "@fortawesome/free-regular-svg-icons";

function ConversationList({ onSelectConversation, activeConversation, conversationUpdate }) {
    const { user, profile } = useUser();
    const [conversations, setConversations] = useState([]);
    const [showNewMessage, setShowNewMessage] = useState(false);

    // Dots menu: { convId, x, y } | null
    const [dotsMenu, setDotsMenu] = useState(null);
    // Delete confirm: convId | null
    const [deleteConfirm, setDeleteConfirm] = useState(null);

    const menuRef = useRef(null);

    // ── FETCH ──────────────────────────────────────────────────────────────────
    const fetchConversations = async () => {
        if (!user) return;

        const { data } = await supabase
            .from("conversations")
            .select(`
                *,
                user1:profiles!conversations_user1_id_fkey(id, username, full_name, avatar_url),
                user2:profiles!conversations_user2_id_fkey(id, username, full_name, avatar_url)
            `)
            .or(`user1_id.eq.${user.id},user2_id.eq.${user.id}`);

        if (!data) return;

        // Fetch per-user settings (pin + unread override)
        const convIds = data.map(c => c.id);
        const { data: settingsData } = await supabase
            .from("conversation_settings")
            .select("conversation_id, is_pinned, is_unread")
            .eq("user_id", user.id)
            .in("conversation_id", convIds);

        const settingsMap = {};
        (settingsData || []).forEach(s => {
            settingsMap[s.conversation_id] = s;
        });

        const formatted = data.map((conv) => {
            const other = conv.user1_id === user.id ? conv.user2 : conv.user1;
            const settings = settingsMap[conv.id] || {};
            return {
                ...conv,
                otherUser: other,
                is_pinned: settings.is_pinned || false,
                is_unread_override: settings.is_unread || false,
            };
        });

        const withLastMessage = await Promise.all(
            formatted.map(async (conv) => {
                const { data: msgData } = await supabase
                    .from("messages")
                    .select("content, created_at, sender_id, is_read")
                    .eq("conversation_id", conv.id)
                    .order("created_at", { ascending: false })
                    .limit(1);
                return { ...conv, lastMessage: msgData?.[0] || null };
            })
        );

        const sorted = sortConversations(withLastMessage);
        setConversations(sorted);
        return sorted;
    };

    const sortConversations = (list) => {
        const pinned = list.filter(c => c.is_pinned);
        const unpinned = list.filter(c => !c.is_pinned);
        const sortByTime = (arr) => arr.sort((a, b) => {
            const at = a.lastMessage?.created_at ? new Date(a.lastMessage.created_at) : new Date(0);
            const bt = b.lastMessage?.created_at ? new Date(b.lastMessage.created_at) : new Date(0);
            return bt - at;
        });
        return [...sortByTime(pinned), ...sortByTime(unpinned)];
    };

    useEffect(() => {
        if (!user) { setConversations([]); return; }
        let mounted = true;
        fetchConversations().then(() => { if (!mounted) return; });
        return () => { mounted = false; };
    }, [user]);

    useEffect(() => {
        if (!conversationUpdate) return;
        setConversations(prev => {
            const updated = prev.map(conv =>
                conv.id === conversationUpdate.convId
                    ? { ...conv, lastMessage: conversationUpdate.message }
                    : conv
            );
            return sortConversations([...updated]);
        });
    }, [conversationUpdate]);

    // Close menu on outside click
    useEffect(() => {
        const handler = (e) => {
            if (menuRef.current && !menuRef.current.contains(e.target)) {
                setDotsMenu(null);
            }
        };
        window.addEventListener("mousedown", handler);
        return () => window.removeEventListener("mousedown", handler);
    }, []);

    // ── UPSERT SETTINGS HELPER ─────────────────────────────────────────────────
    const upsertSetting = async (convId, patch) => {
        await supabase
            .from("conversation_settings")
            .upsert({
                user_id: user.id,
                conversation_id: convId,
                ...patch,
            }, { onConflict: "user_id,conversation_id" });
    };

    // ── PIN ────────────────────────────────────────────────────────────────────
    const handlePin = async (conv) => {
        setDotsMenu(null);
        const newPinned = !conv.is_pinned;

        setConversations(prev => {
            const updated = prev.map(c =>
                c.id === conv.id ? { ...c, is_pinned: newPinned } : c
            );
            return sortConversations([...updated]);
        });

        await upsertSetting(conv.id, {
            is_pinned: newPinned,
            is_unread: conv.is_unread_override,
        });
    };

    // ── MARK AS UNREAD ─────────────────────────────────────────────────────────
    const handleMarkUnread = async (conv) => {
        setDotsMenu(null);
        const newUnread = !conv.is_unread_override;

        setConversations(prev =>
            prev.map(c =>
                c.id === conv.id ? { ...c, is_unread_override: newUnread } : c
            )
        );

        await upsertSetting(conv.id, {
            is_pinned: conv.is_pinned,
            is_unread: newUnread,
        });
    };

    // ── DELETE ─────────────────────────────────────────────────────────────────
    const handleDelete = async (convId) => {
        setDeleteConfirm(null);
        setDotsMenu(null);

        await supabase.from("conversations").delete().eq("id", convId);

        setConversations(prev => prev.filter(c => c.id !== convId));

        // If this was the active conversation, deselect it
        if (activeConversation?.id === convId) {
            onSelectConversation(null);
        }
    };

    // ── DOTS MENU OPEN ─────────────────────────────────────────────────────────
    const openMenu = (e, conv) => {
        e.stopPropagation();
        const rect = e.currentTarget.getBoundingClientRect();
        setDotsMenu({
            conv,
            x: rect.right + 6,
            y: rect.top,
        });
    };

    const formatTime = (date) => {
        const diff = (Date.now() - new Date(date)) / 1000;
        if (diff < 60) return "now";
        if (diff < 3600) return Math.floor(diff / 60) + "m";
        if (diff < 86400) return Math.floor(diff / 3600) + "h";
        if (diff < 604800) return Math.floor(diff / 86400) + "d";
        return Math.floor(diff / 604800) + "w";
    };

    const isUnread = (conv) =>
        conv.is_unread_override ||
        (conv.lastMessage && !conv.lastMessage.is_read && conv.lastMessage.sender_id !== user?.id);

    return (
        <div className="messages-left">

            {/* HEADER */}
            <div className="messages-top">
                <div className="messages-top-header">
                    <h2 className="messages-username">{profile?.username}</h2>
                    <button className="new-message-btn" onClick={() => setShowNewMessage(true)}>
                        <svg fill="currentColor" height="24" viewBox="0 0 24 24" width="24">
                            <path d="M12.202 3.203H5.25a3 3 0 0 0-3 3V18.75a3 3 0 0 0 3 3h12.547a3 3 0 0 0 3-3v-6.952" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" />
                            <path d="M10.002 17.226H6.774v-3.228L18.607 2.165a1.417 1.417 0 0 1 2.004 0l1.224 1.225a1.417 1.417 0 0 1 0 2.004Z" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" />
                            <line fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" x1="16.848" x2="20.076" y1="3.924" y2="7.153" />
                        </svg>
                    </button>
                </div>
                <div className="messages-subheader">
                    <span className="messages-title">Messages</span>
                    {/* <button className="messages-requests-btn">Requests</button> */}
                </div>
            </div>

            {/* EMPTY or LIST */}
            {conversations.length === 0 ? (
                <div className="messages-empty">
                    <svg fill="currentColor" height="96" viewBox="0 0 96 96" width="96">
                        <circle cx="48" cy="48" fill="none" r="47" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" />
                        <path d="m52.309 67.221 17.024-28.643c2.25-3.784-.478-8.578-4.88-8.578H31.55c-5.084 0-7.605 6.169-3.976 9.73l10.574 10.376 3.762 15.55c1.197 4.947 7.798 5.94 10.399 1.565Z" fill="none" stroke="currentColor" strokeLinejoin="round" strokeWidth="2" />
                        <line fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" x1="38.148" x2="55.675" y1="50.106" y2="40.134" />
                    </svg>
                    <h3>Your Messages</h3>
                    <p>Send a message to start a chat.</p>
                    <button className="messages-start-btn" onClick={() => setShowNewMessage(true)}>Send Message</button>
                </div>
            ) : (
                <div className="conversation-list">
                    {conversations.map((conv) => {
                        const unread = isUnread(conv);
                        return (
                            <div
                                key={conv.id}
                                className={`conversation-item ${activeConversation?.id === conv.id ? "active" : ""} ${unread ? "unread" : ""}`}
                                onClick={() => {
                                    // Immediately clear the unread dot in local state
                                    // so it disappears on click without waiting for DB
                                    setConversations(prev => prev.map(c => {
                                        if (c.id !== conv.id) return c;
                                        return {
                                            ...c,
                                            is_unread_override: false,
                                            lastMessage: c.lastMessage
                                                ? { ...c.lastMessage, is_read: true }
                                                : null,
                                        };
                                    }));

                                    // If manually marked unread, clear it in DB silently
                                    if (conv.is_unread_override) {
                                        upsertSetting(conv.id, {
                                            is_pinned: conv.is_pinned,
                                            is_unread: false,
                                        });
                                    }

                                    onSelectConversation(conv);
                                }}
                            >
                                {/* AVATAR */}
                                <div className="conversation-avatar-wrapper">
                                    {conv.otherUser?.avatar_url ? (
                                        <img src={conv.otherUser.avatar_url} className="conversation-avatar" alt="" />
                                    ) : (
                                        <div className="conversation-avatar fallback">
                                            {conv.otherUser?.username?.charAt(0).toUpperCase()}
                                        </div>
                                    )}
                                </div>

                                {/* INFO */}
                                <div className="conversation-info">
                                    <div className="conversation-name-row">
                                        <span className="conversation-name">
                                            {conv.otherUser?.full_name || conv.otherUser?.username}
                                        </span>
                                        {conv.is_pinned && (
                                            <span className="conv-pin-icon" title="Pinned">
                                                <FontAwesomeIcon icon={faThumbtack} />
                                            </span>
                                        )}
                                    </div>

                                    <div className="conversation-preview-row">
                                        <span className="conversation-last">
                                            {conv.lastMessage
                                                ? `${conv.lastMessage.sender_id === user.id ? "You: " : ""}${conv.lastMessage.content}`
                                                : "No messages yet"}
                                        </span>
                                        {conv.lastMessage && (
                                            <>
                                                <span className="conversation-dot">·</span>
                                                <span className="conversation-time">
                                                    {formatTime(conv.lastMessage.created_at)}
                                                </span>
                                            </>
                                        )}
                                    </div>
                                </div>

                                {/* UNREAD DOT + DOTS BUTTON (shown on hover) */}
                                <div className="conv-right-actions">
                                    {unread && (
                                        <span className="conv-unread-dot" />
                                    )}
                                    <button
                                        className="conv-dots-btn"
                                        onClick={(e) => openMenu(e, conv)}
                                        title="Options"
                                    >
                                        <FontAwesomeIcon icon={faEllipsisH} />
                                    </button>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}

            {/* DOTS CONTEXT MENU */}
            {dotsMenu && (
                <div
                    ref={menuRef}
                    className="conv-context-menu"
                    style={{ top: dotsMenu.y, left: dotsMenu.x }}
                    onClick={e => e.stopPropagation()}
                >
                    {/* Mark as unread / read */}
                    <div
                        className="conv-menu-item"
                        onClick={() => handleMarkUnread(dotsMenu.conv)}
                    >
                        <FontAwesomeIcon
                            icon={dotsMenu.conv.is_unread_override ? faCircleRegular : faCircle}
                            className="conv-menu-icon"
                        />
                        {dotsMenu.conv.is_unread_override ? "Mark as read" : "Mark as unread"}
                    </div>

                    <div className="conv-menu-divider" />

                    {/* Pin / Unpin */}
                    <div
                        className="conv-menu-item"
                        onClick={() => handlePin(dotsMenu.conv)}
                    >
                        <FontAwesomeIcon icon={faThumbtack} className="conv-menu-icon" />
                        {dotsMenu.conv.is_pinned ? "Unpin" : "Pin"}
                    </div>

                    <div className="conv-menu-divider" />

                    {/* Delete */}
                    <div
                        className="conv-menu-item danger"
                        onClick={() => {
                            setDotsMenu(null);
                            setDeleteConfirm(dotsMenu.conv.id);
                        }}
                    >
                        <FontAwesomeIcon icon={faTrash} className="conv-menu-icon" />
                        Delete
                    </div>
                </div>
            )}

            {/* DELETE CONFIRM MODAL */}
            {deleteConfirm && (
                <div className="conv-delete-overlay" onClick={() => setDeleteConfirm(null)}>
                    <div className="conv-delete-box" onClick={e => e.stopPropagation()}>
                        <div className="conv-delete-content">
                            <h3>Delete chat?</h3>
                            <p>This will permanently delete the chat and all messages.</p>
                        </div>
                        <div className="conv-delete-divider" />
                        <button
                            className="conv-delete-confirm"
                            onClick={() => handleDelete(deleteConfirm)}
                        >
                            Delete
                        </button>
                        <div className="conv-delete-divider" />
                        <button
                            className="conv-delete-cancel"
                            onClick={() => setDeleteConfirm(null)}
                        >
                            Cancel
                        </button>
                    </div>
                </div>
            )}

            <NewMessageModal
                open={showNewMessage}
                onClose={() => setShowNewMessage(false)}
                onConversationCreated={(conv) => {
                    fetchConversations();
                    onSelectConversation(conv);
                    setShowNewMessage(false);
                }}
            />
        </div>
    );
}

export default ConversationList;
